/**
 * Calls — `modules/scheduling/routes.ts`.
 *
 * Booking is three calls in a row and that is the design, not an accident: the grid
 * is drawn from `availability`, a slot is **held** for ten minutes so the race is
 * closed in Postgres, and only then is the call booked from that hold
 * (`STACK.md` §Calendar). A page that skipped the hold would double-book under two
 * readers on the same slot.
 *
 * None of them is money. A billable call type is charged through an approved extra
 * that already exists (`assertWithinApprovedTime`), so the booking commits nothing
 * new — which is why there is no `Idempotency-Key` here and the hold is what makes
 * a double-click safe.
 */

import { useApiMutation, useApiQuery } from '../hooks.js'
import { queryKeys } from '../keys.js'

/** `GET /api/clients/me/calls` — the next one, the ones after it, and the past. */
export function useCalls() {
  return useApiQuery({
    key: queryKeys.calls.page(),
    request: (api) => api.rest.clients.me.calls.$get(),
  })
}

/** `GET /api/clients/me/calls/rules` — the fold in both zones, and the four types. */
export function useBookingRules() {
  return useApiQuery({
    key: queryKeys.calls.rules(),
    request: (api) => api.rest.clients.me.calls.rules.$get(),
  })
}

/**
 * `GET /api/clients/me/calls/availability?week=&type=` — one week of the grid.
 *
 * `type` is required by the route, so the query is disabled until the reader has
 * chosen one — an availability grid for no call type has no slot length and would
 * be a 400 the page could do nothing about.
 */
export function useAvailability(type: string | undefined, week?: string) {
  return useApiQuery({
    key: queryKeys.calls.availability(week, type),
    request: (api) =>
      api.rest.clients.me.calls.availability.$get({
        query: { type: type ?? '', ...(week === undefined ? {} : { week }) },
      }),
    enabled: type !== undefined && type !== '',
  })
}

/** `POST /api/clients/me/calls/holds` — takes a slot for ten minutes. */
export function useHoldSlot() {
  return useApiMutation({
    module: 'calls',
    request: (api, variables: { slotStart: string; callType: string; minutes?: number }) =>
      api.rest.clients.me.calls.holds.$post({
        json: {
          slot_start: variables.slotStart,
          call_type: variables.callType,
          ...(variables.minutes === undefined ? {} : { minutes: variables.minutes }),
        },
      }),
  })
}

/** `POST /api/clients/me/calls` — the booking itself, from a hold. */
export function useBookCall() {
  return useApiMutation({
    module: 'calls',
    request: (
      api,
      variables: {
        holdId: string
        title: string
        attendees: readonly string[]
        workOrderRef?: string
      },
    ) =>
      api.rest.clients.me.calls.$post({
        json: {
          hold_id: variables.holdId,
          title: variables.title,
          attendees: [...variables.attendees],
          ...(variables.workOrderRef === undefined
            ? {}
            : { work_order_ref: variables.workOrderRef }),
        },
      }),
    alsoInvalidates: ['core'],
  })
}

/** `POST /api/clients/me/calls/:ref/reschedule` — the same call, a new hold. */
export function useRescheduleCall() {
  return useApiMutation({
    module: 'calls',
    request: (api, variables: { ref: string; holdId: string }) =>
      api.rest.clients.me.calls[':ref'].reschedule.$post({
        param: { ref: variables.ref },
        json: { hold_id: variables.holdId },
      }),
    alsoInvalidates: ['core'],
  })
}

/** `POST /api/clients/me/calls/:ref/cancel` — inside the notice window, with a note. */
export function useCancelCall() {
  return useApiMutation({
    module: 'calls',
    request: (api, variables: { ref: string; reason: string }) =>
      api.rest.clients.me.calls[':ref'].cancel.$post({
        param: { ref: variables.ref },
        json: { reason: variables.reason },
      }),
    alsoInvalidates: ['core'],
  })
}
